// Role-Based Access Control (RBAC) Configuration
// Single source of truth for roles, permissions, and navigation visibility

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  CALLER: 'caller',
};

export const ROLE_LABEL_KEYS = {
  super_admin: 'roles.super_admin',
  admin: 'roles.admin',
  manager: 'roles.manager',
  caller: 'roles.caller',
  // Backward compat
  agent: 'roles.caller',
};

// Keep for backward compat (non-i18n contexts)
export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  caller: 'Caller',
  agent: 'Caller',
};

export const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400',
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-400',
  manager: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-400',
  caller: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400',
  // Backward compat
  agent: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400',
};

// Normalize role: lowercase+trim for defensive robustness, then alias legacy/i18n values.
// 'agent' -> 'caller' (legacy), 'gerente' -> 'manager' (es-MX alias)
export const normalizeRole = (role) => {
  if (!role || typeof role !== 'string') return role;
  const r = role.trim().toLowerCase();
  if (r === 'agent') return 'caller';
  if (r === 'gerente') return 'manager';
  return r;
};

// Which roles can see each nav item (by href)
const NAV_PERMISSIONS = {
  '/':              ['super_admin', 'admin', 'manager', 'caller'],
  '/postventa':     ['super_admin', 'admin', 'manager'],
  '/agents':        ['super_admin', 'admin', 'manager'],
  '/tools':         ['super_admin', 'admin', 'manager'],
  '/campaigns':     ['super_admin', 'admin', 'manager', 'caller'],
  '/calls':         ['super_admin', 'admin', 'manager', 'caller'],
  '/call-history':  ['super_admin', 'admin', 'manager', 'caller'],
  '/analytics':     ['super_admin', 'admin', 'manager', 'caller'],
  '/whatsapp':      ['super_admin', 'admin', 'manager', 'caller'],
  '/calendar':      ['super_admin', 'admin', 'manager', 'caller'],
  // User manual lives at /manual (was /docs, which collides with FastAPI Swagger).
  '/manual':        ['super_admin', 'admin', 'manager', 'caller'],
  '/support':       ['super_admin', 'admin', 'manager', 'caller'],
  '/phone-numbers': ['super_admin', 'admin'],
  '/team':          ['super_admin', 'admin', 'manager'],
  // Configuración / Settings: admin edits, manager sees the effective policy read-only.
  '/settings':      ['super_admin', 'admin', 'manager'],
  // /salesperson-profiles route removed — superseded by agent owner_user_id
};

// Granular feature permissions
const FEATURE_PERMISSIONS = {
  // Agents
  'agents.view':    ['super_admin', 'admin', 'manager'],
  'agents.create':  ['super_admin', 'admin'],
  'agents.edit':    ['super_admin', 'admin', 'manager'],
  'agents.delete':  ['super_admin', 'admin'],

  // Tools
  'tools.view':     ['super_admin', 'admin', 'manager'],
  'tools.create':   ['super_admin', 'admin'],
  'tools.edit':     ['super_admin', 'admin', 'manager'],
  'tools.delete':   ['super_admin', 'admin'],
  'tools.test':     ['super_admin', 'admin', 'manager'],

  // Campaigns
  'campaigns.view':   ['super_admin', 'admin', 'manager', 'caller'],
  'campaigns.create': ['super_admin', 'admin', 'manager', 'caller'],
  'campaigns.manage': ['super_admin', 'admin', 'manager'],

  // Calls
  'calls.view':   ['super_admin', 'admin', 'manager', 'caller'],

  // Analytics
  'analytics.view': ['super_admin', 'admin', 'manager', 'caller'],

  // Postventa
  'postventa.view':   ['super_admin', 'admin', 'manager'],
  'postventa.manage': ['super_admin', 'admin', 'manager'],

  // Phone Numbers
  'phoneNumbers.view':    ['super_admin', 'admin'],
  'phoneNumbers.manage':  ['super_admin', 'admin'],

  // Team
  'team.view':            ['super_admin', 'admin', 'manager'],
  'team.invite':          ['super_admin', 'admin', 'manager'],
  'team.invite_any_role': ['super_admin', 'admin'],
  'team.edit_user':       ['super_admin', 'admin', 'manager'],
  'team.edit_role':       ['super_admin', 'admin'],
  'team.delete_user':     ['super_admin', 'admin', 'manager'],
  'team.manage_all':      ['super_admin', 'admin'],

  // salespersonProfiles permissions removed — superseded by agent owner_user_id
};

// Route-level access (maps route patterns to allowed roles)
export const ROUTE_ACCESS = {
  '/':              ['super_admin', 'admin', 'manager', 'caller'],
  '/postventa':     ['super_admin', 'admin', 'manager'],
  '/agents':        ['super_admin', 'admin', 'manager'],
  '/agents/new':    ['super_admin', 'admin', 'manager'], // managers reach this via child-create modal
  '/agents/:id/edit': ['super_admin', 'admin', 'manager'],
  '/tools':         ['super_admin', 'admin', 'manager'],
  '/campaigns':     ['super_admin', 'admin', 'manager', 'caller'],
  '/campaigns/new': ['super_admin', 'admin', 'manager', 'caller'],
  '/campaigns/:id': ['super_admin', 'admin', 'manager', 'caller'],
  '/calls':         ['super_admin', 'admin', 'manager', 'caller'],
  '/call-history':  ['super_admin', 'admin', 'manager', 'caller'],
  '/analytics':     ['super_admin', 'admin', 'manager', 'caller'],
  '/whatsapp':      ['super_admin', 'admin', 'manager', 'caller'],
  '/calendar':      ['super_admin', 'admin', 'manager', 'caller'],
  // User manual lives at /manual (was /docs, which collides with FastAPI Swagger).
  '/manual':        ['super_admin', 'admin', 'manager', 'caller'],
  // Legacy /docs kept so the in-app redirect (/docs -> /manual) passes the access check.
  '/docs':          ['super_admin', 'admin', 'manager', 'caller'],
  // Support / Soporte contact tab (email + WhatsApp direct lines).
  '/support':       ['super_admin', 'admin', 'manager', 'caller'],
  '/phone-numbers': ['super_admin', 'admin'],
  '/team':          ['super_admin', 'admin', 'manager'],
  // Configuración / Settings: admin edits, manager sees the effective policy read-only.
  '/settings':      ['super_admin', 'admin', 'manager'],
  // /salesperson-profiles route removed — superseded by agent owner_user_id
};

/**
 * Check if a role can access a specific feature
 */
export const canAccess = (role, featureKey) => {
  const normalized = normalizeRole(role);
  const allowed = FEATURE_PERMISSIONS[featureKey];
  if (!allowed) return false;
  return allowed.includes(normalized);
};

/**
 * Check if a role can see a nav item
 */
export const canSeeNav = (role, href) => {
  const normalized = normalizeRole(role);
  const allowed = NAV_PERMISSIONS[href];
  if (!allowed) return false;
  return allowed.includes(normalized);
};

// Tenant-layout gating (config-driven, no per-user role change).
// Some modules only make sense for certain tenant analytics_layout values.
// Postventa is an automotive after-sales module; it must be hidden for pure
// cobranza (collections) tenants regardless of the user's role.
// Driven entirely by the tenant record's analytics_layout, so hiding/un-hiding
// is a config change on the tenant (analytics_layout), not a code edit.
const POSTVENTA_HIDDEN_LAYOUTS = ['cobranza'];

/**
 * Whether the Postventa module (nav item + route) should be hidden for a tenant.
 * Returns true when the tenant's analytics_layout is a layout that does not use
 * postventa (currently: cobranza). Safe on null/undefined tenant (returns false).
 * @param {{analytics_layout?: string}|null|undefined} tenant
 */
export const isPostventaHiddenForTenant = (tenant) => {
  const layout = tenant?.analytics_layout;
  if (!layout || typeof layout !== 'string') return false;
  return POSTVENTA_HIDDEN_LAYOUTS.includes(layout.trim().toLowerCase());
};

/**
 * Check if a role can access a route
 * Matches exact routes and parameterized routes
 */
export const canAccessRoute = (role, pathname) => {
  const normalized = normalizeRole(role);

  // Try exact match first
  if (ROUTE_ACCESS[pathname]) {
    return ROUTE_ACCESS[pathname].includes(normalized);
  }

  // Try parameterized route matching
  // /agents/123/edit -> /agents/:id/edit
  if (/^\/agents\/[^/]+\/edit$/.test(pathname)) {
    return ROUTE_ACCESS['/agents/:id/edit'].includes(normalized);
  }
  // /campaigns/123 -> /campaigns/:id
  if (/^\/campaigns\/[^/]+$/.test(pathname)) {
    return ROUTE_ACCESS['/campaigns/:id'].includes(normalized);
  }

  // Default: allow (route not restricted)
  return true;
};

/**
 * Get invite role options based on the inviter's role
 */
export const getInviteRoleOptions = (role) => {
  const normalized = normalizeRole(role);
  if (['super_admin', 'admin'].includes(normalized)) {
    return [
      { value: 'caller', label: 'Caller - Can make calls' },
      { value: 'manager', label: 'Manager - Can view team stats' },
      { value: 'admin', label: 'Admin - Full access' },
    ];
  }
  if (normalized === 'manager') {
    return [
      { value: 'caller', label: 'Caller - Can make calls' },
    ];
  }
  return [];
};
